// How the list is laid out for one store: its sections in walking order, what's in the cart,
// and what's only for other stores. Pure, so it's easy to test.
import { itemAtStore, orderedCategories, type Category, type Item, type Store } from '@milk/shared';

export interface ListView {
  sections: { category: Category; items: Item[] }[];
  /** Checked off (shown at the bottom, newest first). */
  cart: Item[];
  /** Still to buy, but only at other stores. */
  elsewhere: Item[];
  /** Still to buy here. */
  toBuy: number;
}

const byName = (a: Item, b: Item) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });

export function buildList(items: Item[], categories: Category[], store: Store | null): ListView {
  const order = orderedCategories(store, categories);
  const known = new Set(order.map((c) => c.id));
  const here: Item[] = [];
  const elsewhere: Item[] = [];
  const cart: Item[] = [];
  for (const it of items) {
    if (it.checked) { if (itemAtStore(it, store?.id ?? null)) cart.push(it); continue; }
    (itemAtStore(it, store?.id ?? null) ? here : elsewhere).push(it);
  }
  const fallback = order.find((c) => c.id === 'other') ?? order[order.length - 1];
  const groups = new Map<string, Item[]>();
  for (const it of here) {
    const cid = known.has(it.categoryId) ? it.categoryId : fallback?.id ?? it.categoryId;
    groups.set(cid, [...(groups.get(cid) ?? []), it]);
  }
  return {
    sections: order.filter((c) => groups.has(c.id)).map((c) => ({ category: c, items: groups.get(c.id)!.sort(byName) })),
    cart: cart.sort((a, b) => (b.checkedAt ?? '').localeCompare(a.checkedAt ?? '')),
    elsewhere: elsewhere.sort(byName),
    toBuy: here.length,
  };
}
